import { useState, useCallback, useRef, useEffect } from 'react';
import { billingApi, PaymentIntentResponse } from '../services/billingApi';
import logger from '../utils/logger';
// Payment intent expires after 10 minutes, refresh at 8 minutes
const INTENT_REFRESH_INTERVAL = 8 * 60 * 1000; // 8 minutes in ms

interface PaymentIntentState {
    googlePay: PaymentIntentResponse | null;
    applePay: PaymentIntentResponse | null;
}

interface UsePaymentIntentReturn {
    intents: PaymentIntentState;
    isLoading: boolean;
    error: string | null;
    createGooglePayIntent: () => Promise<PaymentIntentResponse | null>;
    createApplePayIntent: () => Promise<PaymentIntentResponse | null>;
    refreshIntents: () => Promise<void>;
    clearIntents: () => void;
    recreateIntents: () => void; // Trigger fresh intent creation (e.g., after coupon change)
    recreationKey: number; // Incremented when recreation is requested, for useEffect dependencies
}

interface UsePaymentIntentProps {
    amount: number;
    currencyCode: string;
    planId: string;
    customerId?: string;
    enabled?: boolean;
}

/**
 * Hook to manage payment intents for digital wallets
 * Handles creation, refresh, and expiration of payment intents
 */
export function usePaymentIntent({
    amount,
    currencyCode,
    planId,
    customerId,
    enabled = true,
}: UsePaymentIntentProps): UsePaymentIntentReturn {
    const [intents, setIntents] = useState<PaymentIntentState>({
        googlePay: null,
        applePay: null,
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // Key to trigger intent recreation (incremented when recreateIntents is called)
    const [recreationKey, setRecreationKey] = useState(0);

    const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
    const lastAmountRef = useRef<number>(amount);
    const lastPlanIdRef = useRef<string>(planId);

    // Clear intents when disabled or amount changes
    useEffect(() => {
        if (!enabled || amount <= 0) {
            clearIntents();
        }
    }, [enabled, amount]);

    // Track when amount or plan changes to trigger refresh
    useEffect(() => {
        if (
            (amount !== lastAmountRef.current ||
                planId !== lastPlanIdRef.current) &&
            enabled &&
            amount > 0
        ) {
            lastAmountRef.current = amount;
            lastPlanIdRef.current = planId;
            // Intents need to be recreated with new amount
            clearIntents();
        }
    }, [amount, planId, enabled]);

    // Cleanup timer on unmount
    useEffect(() => {
        return () => {
            if (refreshTimerRef.current) {
                clearTimeout(refreshTimerRef.current);
            }
        };
    }, []);

    // Start refresh timer
    const startRefreshTimer = useCallback(() => {
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
        }

        refreshTimerRef.current = setTimeout(() => {
            logger.debug(
                '[PAYMENT-INTENT] Refreshing intents due to expiration'
            );
            refreshIntents();
        }, INTENT_REFRESH_INTERVAL);
    }, []);

    // Create Google Pay payment intent
    const createGooglePayIntent =
        useCallback(async (): Promise<PaymentIntentResponse | null> => {
            if (!enabled || amount <= 0) {
                logger.debug(
                    '[PAYMENT-INTENT] Skipping Google Pay intent - disabled or $0'
                );
                return null;
            }

            try {
                logger.debug('[PAYMENT-INTENT] Creating Google Pay intent:', {
                    amount,
                    currencyCode,
                    planId,
                });

                const intent = await billingApi.createPaymentIntent(
                    amount,
                    currencyCode,
                    'google_pay',
                    planId,
                    customerId
                );

                setIntents((prev) => ({ ...prev, googlePay: intent }));
                startRefreshTimer();

                logger.debug(
                    '[PAYMENT-INTENT] Google Pay intent created:',
                    intent
                );
                return intent;
            } catch (err) {
                console.error(
                    '[PAYMENT-INTENT] Failed to create Google Pay intent:',
                    err
                );
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to create payment intent'
                );
                return null;
            }
        }, [
            amount,
            currencyCode,
            planId,
            customerId,
            enabled,
            startRefreshTimer,
        ]);

    // Create Apple Pay payment intent
    const createApplePayIntent =
        useCallback(async (): Promise<PaymentIntentResponse | null> => {
            if (!enabled || amount <= 0) {
                logger.debug(
                    '[PAYMENT-INTENT] Skipping Apple Pay intent - disabled or $0'
                );
                return null;
            }

            try {
                logger.debug('[PAYMENT-INTENT] Creating Apple Pay intent:', {
                    amount,
                    currencyCode,
                    planId,
                });

                const intent = await billingApi.createPaymentIntent(
                    amount,
                    currencyCode,
                    'apple_pay',
                    planId,
                    customerId
                );

                setIntents((prev) => ({ ...prev, applePay: intent }));
                startRefreshTimer();

                logger.debug(
                    '[PAYMENT-INTENT] Apple Pay intent created:',
                    intent
                );
                return intent;
            } catch (err) {
                console.error(
                    '[PAYMENT-INTENT] Failed to create Apple Pay intent:',
                    err
                );
                setError(
                    err instanceof Error
                        ? err.message
                        : 'Failed to create payment intent'
                );
                return null;
            }
        }, [
            amount,
            currencyCode,
            planId,
            customerId,
            enabled,
            startRefreshTimer,
        ]);

    // Refresh all active intents
    const refreshIntents = useCallback(async () => {
        if (!enabled || amount <= 0) {
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const promises: Promise<PaymentIntentResponse | null>[] = [];

            // Only refresh intents that were previously created
            if (intents.googlePay) {
                promises.push(createGooglePayIntent());
            }
            if (intents.applePay) {
                promises.push(createApplePayIntent());
            }

            await Promise.all(promises);
        } catch (err) {
            console.error('[PAYMENT-INTENT] Failed to refresh intents:', err);
            setError(
                err instanceof Error
                    ? err.message
                    : 'Failed to refresh payment intents'
            );
        } finally {
            setIsLoading(false);
        }
    }, [
        enabled,
        amount,
        intents.googlePay,
        intents.applePay,
        createGooglePayIntent,
        createApplePayIntent,
    ]);

    // Clear all intents
    const clearIntents = useCallback(() => {
        logger.debug('[PAYMENT-INTENT] Clearing intents');
        setIntents({ googlePay: null, applePay: null });
        setError(null);
        if (refreshTimerRef.current) {
            clearTimeout(refreshTimerRef.current);
            refreshTimerRef.current = null;
        }
    }, []);

    // Trigger recreation of all intents (e.g., after coupon change)
    // This clears existing intents and increments the recreation key,
    // which signals to the parent component that new intents need to be created
    const recreateIntents = useCallback(() => {
        logger.debug('[PAYMENT-INTENT] Triggering intent recreation');
        clearIntents();
        setRecreationKey((prev) => prev + 1);
    }, [clearIntents]);

    return {
        intents,
        isLoading,
        error,
        createGooglePayIntent,
        createApplePayIntent,
        refreshIntents,
        clearIntents,
        recreateIntents,
        recreationKey, // Expose so parent can react to recreation requests
    };
}

export default usePaymentIntent;
